<template>
  <div class="document-translation">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <h2 class="page-title">{{ projectInfo?.title || t('document.pageTitle') }}</h2>
      </div>
      <div class="toolbar-right">
        <a-space>
          <!-- 视图模式切换 -->
          <a-radio-group 
            :value="viewMode" 
            @change="handleViewModeChange" 
            size="small"
          >
            <a-radio-button value="preview">{{ t('document.preview') }}</a-radio-button>
            <a-radio-button 
              v-if="projectInfo?.status !== 'completed'"
              value="sentence"
              :disabled="isButtonsDisabled"
            >
              {{ t('document.sentence') }}
            </a-radio-button>
          </a-radio-group>
          
          <!-- 工作区 ActionBar：完成项目 / 指派 / 提交 -->
          <WorkItemActionBar
            v-if="projectId"
            ref="actionBarRef"
            :work-item-id="projectId"
            :current-user-id="_userStore.userInfo?.id || _userStore.user?.id || 0"
            @completed="onWorkItemCompleted"
            @assigned="onWorkItemAssigned"
            @submitted="onWorkItemSubmitted"
          />

          <!-- 保存按钮 -->
          <a-button 
            v-if="hasActiveTask && viewMode === 'sentence'"
            @click="saveAllEdits" 
            :loading="saving"
          >
            <template #icon><SaveOutlined /></template>
            {{ t('document.saveAllEdits') }}
          </a-button>
          
          <!-- 语言切换 -->
          <a-select
            v-if="hasActiveTask"
            v-model:value="currentTargetLanguage"
            :placeholder="t('document.selectTargetLanguage')"
            style="width: 120px"
            disabled
            @change="handleLanguageChange"
          >
            <a-select-option
              v-for="lang in targetLanguages"
              :key="lang"
              :value="lang"
            >
              {{ getLanguageName(lang) }}
            </a-select-option>
          </a-select>
          
          
          <!-- 导出文档（仅完成任务后显示） -->
          <a-button
            v-if="projectInfo?.status === 'completed'"
            @click="exportDocument"
            :loading="exportingDocument"
          >
            <template #icon><ExportOutlined /></template>
            {{ t('document.exportDocument') }}
          </a-button>
        </a-space>
      </div>
    </div>
    
    <!-- 同步进度提示 -->
    <a-modal 
      v-model:open="syncing" 
      :footer="null" 
      :closable="false"
      :maskClosable="false"
      :width="300"
      :centered="true"
      class="sync-progress-modal"
    >
      <div class="sync-progress">
        <a-spin size="large" />
        <p>{{ t('document.syncing') }}</p>
      </div>
    </a-modal>

    <ReviseCommentBanner
      :task-id="currentReviewTaskId"
      :round="currentReviewRound"
    />

    <!-- 主要内容区域 -->
    <div class="main-content" v-if="hasActiveTask">
      <a-spin :spinning="loading" :tip="t('document.loading')">
        <!-- 文档翻译工作区 -->
        <div class="translation-workspace">
          <!-- 逐句对照模式 -->
          <div v-if="viewMode === 'sentence'" class="sentence-mode">
            <SentenceEditor 
              :sentences="sentences"
              :editable="hasActiveTask"
              :loading="sentencesLoading"
              @update="handleSentenceUpdate"
              @save="handleSentenceSave"
            />
          </div>
          
          <!-- 整页预览模式 -->
          <div v-else-if="viewMode === 'preview'" class="preview-mode">
            <div class="document-panels">
              <!-- 原文文档 -->
              <div class="panel source-panel">
                <h3>{{ t('document.source') }}</h3>
                <div class="panel-content">
                  <!-- 文档 PDF 预览（后端 LibreOffice 转 PDF） -->
                  <DocumentViewer 
                    v-if="sourceDocumentUrl && sourceFileType === 'pdf'"
                    :document-url="sourceDocumentUrl"
                    :file-type="sourceFileType"
                    ref="sourceViewer"
                    :enable-sync-scroll="true"
                    @page-change="handleSourcePageChange"
                    @sync-scroll="handleSourceScroll"
                  />
                  <div v-else-if="!sourceDocumentUrl" class="no-document">
                    <a-empty :description="t('document.documentNotLoaded')" />
                  </div>
                  <div v-else class="no-document">
                    <a-result
                      status="warning"
                      :title="t('document.unsupportedFileFormat')"
                      :sub-title="t('document.unsupportedPreviewSubtitle', { ext: sourceFileType })"
                    >
                      <template #extra>
                        <a-button type="primary" @click="handleDownloadSource">
                          {{ t('document.downloadDocument') }}
                        </a-button>
                      </template>
                    </a-result>
                  </div>
                </div>
              </div>
              
              <!-- 译文文档 -->
              <div class="panel target-panel">
                <h3>{{ t('document.target') }}</h3>
                <div class="panel-content">
                  <!-- 文档 PDF 预览（后端 LibreOffice 转 PDF） -->
                  <DocumentViewer 
                    v-if="targetDocumentUrl && targetFileType === 'pdf'"
                    :key="`target-${targetViewerKey}`"
                    :document-url="targetDocumentUrl"
                    :file-type="targetFileType"
                    ref="targetViewer"
                    :enable-sync-scroll="true"
                    @page-change="handleTargetPageChange"
                    @sync-scroll="handleTargetScroll"
                  />
                  <div v-else-if="!targetDocumentUrl" class="no-document">
                    <a-empty :description="t('document.translationNotGenerated')" />
                  </div>
                  <div v-else class="no-document">
                    <a-result
                      status="warning"
                      :title="t('document.unsupportedFileFormat')"
                      :sub-title="t('document.unsupportedPreviewSubtitle', { ext: targetFileType })"
                    >
                      <template #extra>
                        <a-button type="primary" @click="handleDownloadTarget">
                          {{ t('document.downloadDocument') }}
                        </a-button>
                      </template>
                    </a-result>
                  </div>
                </div>
              </div>
              
              <!-- 热词管理 -->
              <div class="panel hotword-panel">
                <a-card :title="t('document.hotwordManagement')" class="hotword-card">
                  <template #extra>
                    <a-space>
                      <!-- 隐藏提取热词按钮，因为会自动提取 -->
                      <a-button 
                        type="dashed" 
                        size="small" 
                        @click="showAddHotwordModal"
                        :disabled="isEditingDisabled"
                      >
                        <template #icon><PlusOutlined /></template>
                      </a-button>
                      <a-button size="small" @click="refreshHotWords" :disabled="isEditingDisabled">
                        <template #icon><ReloadOutlined /></template>
                      </a-button>
                    </a-space>
                  </template>

                  <!-- 热词统计 -->
                  <div class="hotword-stats" v-if="hotWordStats">
                    <a-row :gutter="4">
                      <a-col :span="8">
                        <a-statistic :title="t('document.totalCount')" :value="hotWordStats.total_count" />
                      </a-col>
                      <a-col :span="8">
                        <a-statistic :title="t('document.hotwordStatusAiSuggested')" :value="hotWordStats.ai_suggested_count" />
                      </a-col>
                      <a-col :span="8">
                        <a-statistic :title="t('document.dictionaryMatched')" :value="hotWordStats.from_dictionary_count" />
                      </a-col>
                    </a-row>
                  </div>

                  <a-divider style="margin: 8px 0;" />

                  <!-- 热词列表 -->
                  <div class="hotword-list">
                    <a-table 
                      :dataSource="hotWords" 
                      :columns="hotWordColumns"
                      :pagination="false"
                      size="small"
                      :scroll="{ y: 400 }"
                      rowKey="id"
                    >
                      <template #bodyCell="{ column, record }">
                        <!-- 热词列 -->
                        <template v-if="column.key === 'original_word'">
                          <span class="hotword-text">{{ record.original_word }}</span>
                        </template>
                        
                        <!-- 翻译列 -->
                        <template v-if="column.key === 'translated_word'">
                          <a-input
                            v-if="editingHotWordId === record.id"
                            v-model:value="editingHotWordText"
                            size="small"
                            @pressEnter="saveHotWordEdit(record)"
                            @blur="handleHotWordBlur(record)"
                            ref="hotWordInput"
                            style="width: 100%"
                          />
                          <span 
                            v-else 
                            @click="!isEditingDisabled && startHotWordEdit(record)"
                            :class="['translated-word-clickable', { 'disabled': isEditingDisabled }]"
                          >
                            {{ record.translated_word || (isEditingDisabled ? t('document.notEditable') : t('document.clickToEdit')) }}
                          </span>
                        </template>
                        
                        <!-- 状态列 -->
                        <template v-if="column.key === 'status'">
                          <a-tag size="small" :color="getHotWordStatusColor(record.status)">
                            {{ getHotWordStatusText(record.status) }}
                          </a-tag>
                        </template>
                        
                        <!-- 操作列 -->
                        <template v-if="column.key === 'actions'">
                          <a-space size="small">
                            <a-button 
                              v-if="record.status === 'ai_suggested' || record.status === 'edited'"
                              type="text" 
                              size="small"
                              @click="confirmHotWord(record)"
                            >
                              <CheckOutlined />
                            </a-button>
                            <a-button 
                              type="text" 
                              size="small" 
                              danger
                              @click="deleteHotWord(record)"
                            >
                              <DeleteOutlined />
                            </a-button>
                          </a-space>
                        </template>
                      </template>
                    </a-table>
                  </div>

                  <!-- 热词操作 -->
                  <div class="hotword-actions">
                    <a-button 
                      type="primary" 
                      size="small" 
                      block
                      @click="confirmAllHotWords"
                      :disabled="isEditingDisabled || hotWords.length === 0"
                    >
                      {{ t('document.confirmAllHotwords') }}
                    </a-button>
                  </div>
                </a-card>
              </div>
            </div>
          </div>
        </div>
      </a-spin>
    </div>

    <!-- 无任务状态 -->
    <div v-else class="no-task-state">
      <a-empty 
        :description="t('document.pleaseSelectTask')"
        :image="Empty.PRESENTED_IMAGE_SIMPLE"
      >
        <a-button type="primary" @click="startTranslationIfNeeded">
          {{ t('document.startMachineTranslation') }}
        </a-button>
      </a-empty>
    </div>

    <!-- 任务选择模态框 -->
    <TaskSelectionModal
      v-model:visible="taskSelectionVisible"
      :project-id="projectId"
      :project-type="'document'"
      :project-title="projectInfo?.title"
      :source-language="projectInfo?.original_language"
      :target-languages="projectInfo?.target_languages"
      @task-assigned="handleTaskAssigned"
      @cancel="taskSelectionVisible = false"
    />

    <!-- 添加热词模态框 -->
    <a-modal
      v-model:open="addHotwordModalVisible"
      :title="t('document.addHotword')"
      @ok="addHotword"
      @cancel="resetAddHotwordForm"
    >
      <a-form :model="addHotwordForm" layout="vertical">
        <a-form-item :label="t('document.originalText')" required>
          <a-input v-model:value="addHotwordForm.original_word" :placeholder="t('document.enterOriginalText')" />
        </a-form-item>
        <a-form-item :label="t('document.translatedText')" required>
          <a-input v-model:value="addHotwordForm.translated_word" :placeholder="t('document.enterTranslatedText')" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { message, Empty } from 'ant-design-vue'
import {
  SendOutlined,
  SaveOutlined,
  CheckOutlined,
  DownOutlined,
  ExportOutlined,
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
} from '@ant-design/icons-vue'
import TaskSelectionModal from '@/modules/task/components/TaskSelectionModal.vue'
import WorkItemActionBar from '@/modules/project/components/WorkItemActionBar.vue'
import ReviseCommentBanner from '@/modules/task/components/ReviseCommentBanner.vue'
import { getWorkItem } from '@/modules/project/api/work-item'

import DocumentViewer from '@/modules/document/components/DocumentViewer.vue'
import SentenceEditor from '@/modules/document/components/SentenceEditor.vue'
import { 
  getDocumentProject,
  startDocumentTranslation,
  getTranslationProgress,
  completeDocumentTranslation,
  downloadTranslatedDocument,
  getDocumentPdfPreviewUrl,
  segmentDocuments,
  getSentences,
  updateSentences,
  syncToPreview,
  type DocumentProject,
  type DocumentSentence,
  type SentenceUpdate
} from '@/modules/document/api'
import { 
  hotWordApi,
  type HotWord,
  type HotWordStats
} from '@/modules/system/api/hotword'
import { LanguageAPI, type Language } from '@/modules/system/api/language'
import { runApiBatch } from '@/shared/api/use-api-batch'
// 任务通知已由后端自动发送，不再需要前端调用
import { useUserStore } from '@/stores'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const _userStore = useUserStore()

// 基础状态
const projectId = computed(() => {
  const id = route.params.id as string
  const parsedId = parseInt(id)
  if (isNaN(parsedId) || !id) {
    console.error('无效的项目ID:', id, '路由参数:', route.params)
    return NaN
  }
  return parsedId
})
const currentReviewTaskId = ref<number | null>(null)
const currentReviewRound = ref(0)

async function loadReviewTaskContext() {
  if (!projectId.value || Number.isNaN(projectId.value)) return
  try {
    const wi = await getWorkItem(projectId.value)
    currentReviewTaskId.value = wi.current_task_id ?? wi.current_task?.id ?? null
    currentReviewRound.value = wi.current_task?.round ?? 0
  } catch (e) {
    console.error(e)
    currentReviewTaskId.value = null
    currentReviewRound.value = 0
  }
}

const loading = ref(false)
const saving = ref(false)
const completing = ref(false)
const translating = ref(false)
const extractingHotWords = ref(false)

// 翻译状态管理
const translationStatus = ref<'idle' | 'translating' | 'completed' | 'failed'>('idle')
const translationError = ref<string>('')
const translationLock = ref(false)
const translationProgress = ref<any>(null) // 保存完整的翻译进度对象，用于判断是否正在提取热词

// 项目和任务状态
const projectInfo = ref<DocumentProject>()
const hasActiveTask = ref(false)
const actionBarRef = ref<{ refresh: () => Promise<void> } | null>(null)
const currentTargetLanguage = ref<string>('')
const targetLanguages = ref<string[]>([])
const taskSelectionVisible = ref(false)

// 热词管理
const hotWords = ref<HotWord[]>([])
const hotWordStats = ref<HotWordStats>()
const editingHotWordId = ref<number>(-1)
const editingHotWordText = ref('')
const hotWordInput = ref()
const addHotwordModalVisible = ref(false)

// 新增的视图模式管理
const viewMode = ref<'preview' | 'sentence'>('preview')

// 调试函数：打印当前状态
const debugViewMode = () => {
  console.log('🔍 当前状态调试:')
  console.log('  - viewMode.value:', viewMode.value)
  console.log('  - sentences.length:', sentences.value.length)
  console.log('  - sourceDocumentUrl:', sourceDocumentUrl.value)
  console.log('  - targetDocumentUrl:', targetDocumentUrl.value)
}
const syncing = ref(false)
const exportingDocument = ref(false)

// 文档预览相关（仅支持原始文档：.docx, .xlsx, .pptx）
const sourceDocumentUrl = ref('')
const targetDocumentUrl = ref('')
const sourceFileType = ref('')
const targetFileType = ref('')
const sourceViewer = ref()
/** 译文文档查看器实例，模板中 ref="targetViewer" 使用 */
const targetViewer = ref()
// 用于强制重新渲染组件的key
const targetViewerKey = ref(0)

// 同步滚动防循环机制
const isSyncing = ref(false)

// 逐句对照相关
const sentences = ref<DocumentSentence[]>([])
const sentencesLoading = ref(false)
const sentencesPage = ref(1)
const sentencesLimit = ref(80)
const sentencesTotal = ref(0)

// 无任务状态表单
const addHotwordForm = ref({
  original_word: '',
  translated_word: ''
})
// 热词管理标签页
const hotwordActiveTab = ref('management')
const hotwordSearchKeyword = ref('')
const isEditingDisabled = ref(false)
const editingHotword = ref<number>(-1)

// 热词表格列定义
const hotWordColumns = computed(() => [
  {
    title: t('document.hotword'),
    dataIndex: 'original_word',
    key: 'original_word',
    width: 80
  },
  {
    title: t('document.hotwordTranslation'),
    dataIndex: 'translated_word',
    key: 'translated_word',
    width: 120
  },
  {
    title: t('document.status'),
    dataIndex: 'status',
    key: 'status',
    width: 60
  },
  {
    title: t('document.actions'),
    key: 'actions',
    width: 80
  }
])


// 语言数据
const availableLanguages = ref<Language[]>([])

// DOM引用
const _editingTextareaRef = ref<any>()
const hotwordInputRef = ref<any>()


// 计算属性
const _filteredHotWords = computed(() => {
  if (!hotwordSearchKeyword.value) return hotWords.value
  
  const keyword = hotwordSearchKeyword.value.toLowerCase()
  return hotWords.value.filter(hw => 
    hw.original_word.toLowerCase().includes(keyword) ||
    hw.translated_word.toLowerCase().includes(keyword)
  )
})

// 判断是否正在加载中
const isLoading = computed(() => {
  return loading.value || sentencesLoading.value
})

// 判断原文和译文是否已加载完成
const isContentLoaded = computed(() => {
  // 如果没有活跃任务，不检查内容加载状态
  if (!hasActiveTask.value) {
    return true
  }
  
  // 整页预览模式：检查文档 URL
  if (viewMode.value === 'preview') {
    const hasSourceDoc = !!sourceDocumentUrl.value
    const hasTargetDoc = !!targetDocumentUrl.value
    return hasSourceDoc && hasTargetDoc
  }
  
  // 逐句对照模式：检查句子数据，且至少有一个句子有原文和译文
  if (viewMode.value === 'sentence') {
    if (sentences.value.length === 0) {
      return false
    }
    // 检查是否有至少一个句子包含原文和译文
    const hasContent = sentences.value.some(sentence => 
      sentence.source_text && sentence.source_text.trim() !== '' &&
      sentence.target_text && sentence.target_text.trim() !== ''
    )
    return hasContent
  }
  
  // 默认情况：如果不在加载中，认为内容已加载（避免按钮一直禁用）
  return !isLoading.value
})

// 判断按钮是否应该禁用（加载中或内容未加载完成）
const isButtonsDisabled = computed(() => {
  // 如果正在加载，禁用按钮
  if (isLoading.value) {
    return true
  }
  
  // 如果有活跃任务，检查内容是否已加载完成
  if (hasActiveTask.value) {
    return !isContentLoaded.value
  }
  
  // 如果没有活跃任务，不禁用按钮
  return false
})

// 生命周期
onMounted(async () => {
  void loadReviewTaskContext()
  
  try {
    // 如果是任务选择步骤，只加载必要数据并显示对话框
    if (route.query.step === 'task_selection') {
      // 只加载项目基本信息（用于任务选择对话框）
      await loadProjectInfoForTaskSelection()
      // 加载语言列表（用于任务选择对话框）
      await loadLanguages()
      // 延迟一下确保组件完全加载
      await nextTick()
      showTaskSelection()
      return
    }
    
    // 正常流程：加载完整项目信息
    await loadProjectInfo()
    await loadLanguages()
    
    // 如果有活跃任务，加载文档预览
    if (hasActiveTask.value) {
      await loadDocumentPreview()
    }
    
  } catch (error) {
    console.error('文档翻译页面初始化失败:', error)
  }
})

// 方法实现
// 仅加载项目基本信息（用于任务选择对话框）
const loadProjectInfoForTaskSelection = async () => {
  try {
    loading.value = true
    const project = await getDocumentProject(projectId.value)
    projectInfo.value = project
    // 不设置 hasActiveTask，不加载翻译进度、热词等
  } catch (error) {
    console.error('加载项目信息失败:', error)
  } finally {
    loading.value = false
  }
}

const loadProjectInfo = async () => {
  try {
    loading.value = true
    const project = await getDocumentProject(projectId.value)
    projectInfo.value = project
    
    // 检查是否有活跃任务
    if (project.status !== 'draft') {
      hasActiveTask.value = true
      targetLanguages.value = project.target_languages || []
      if (targetLanguages.value.length > 0) {
        currentTargetLanguage.value = targetLanguages.value[0]
      }
      
      // 加载翻译进度，用于判断是否正在提取热词
      try {
        const progress = await getTranslationProgress(projectId.value)
        translationProgress.value = progress
      } catch (error) {
        // 如果获取进度失败，不影响主流程
        console.error('加载翻译进度失败:', error)
      }
      
      await loadHotWords()
      
      // 检查译文文档是否存在，如果不存在则自动触发翻译
      // 需要同时检查 translated_document_storage_id（OSS模式）和 translated_document_url（旧模式）
      const hasTranslatedDocument = project.translated_document_storage_id || project.translated_document_url
      
      if (!hasTranslatedDocument) {
        console.log('译文文档不存在，自动触发翻译')
        await autoStartTranslation()
      } else {
        // 如果有翻译数据，检查是否需要自动提取热词
        // 注意：这里不依赖 sentences，因为整页预览模式下可能不会加载 sentences
        // 只检查翻译文档URL和热词数量
        // 先检查是否正在提取中（通过翻译进度状态）
        const isExtractingInProgress = translationProgress.value?.status === 'extracting_hot_words'
        
        // 如果已有热词或正在提取中，不需要再次提取
        if (hotWords.value.length === 0 && !isExtractingInProgress && !extractingHotWords.value) {
          // 直接触发提取，不需要延迟
          // autoExtractHotWords 内部已经有完整的防重复检查逻辑
          autoExtractHotWords().catch(error => {
            console.error('后台热词提取失败:', error)
          })
        }
      }
    } else {
    }
  } catch (error) {
    console.error('加载项目信息失败:', error)
  } finally {
    loading.value = false
  }
}

// 新增的视图模式相关方法
const handleViewModeChange = async (e: any) => {
  console.log('\n=== 📺 视图模式切换 ===')
  
  const oldMode = viewMode.value
  const newMode = e.target.value
  
  console.log('点击前状态:', oldMode)
  console.log('点击后值:', newMode)
  
  debugViewMode()
  
  console.log('====================\n')
  
  // 如果没有变化，但有未加载的内容，仍然需要加载
  if (oldMode === newMode) {
    console.log('⚠️ 视图模式没有变化')
    
    // 如果切换到逐句对照模式，但句子数据为空，仍需要加载
    if (newMode === 'sentence' && sentences.value.length === 0) {
      console.log('🔧 虽然模式没变，但句子数据为空，开始加载...')
      await loadSentences()
    } else {
      console.log('⏭️ 跳过处理（无需加载）')
      return
    }
    return
  }
  
  // 更新视图模式（先更新，确保UI立即响应）
  viewMode.value = newMode
  console.log(`🔄 视图模式已更新: ${oldMode} -> ${newMode}`)
  
  // 从逐句对照切换到整页预览
  if (oldMode === 'sentence' && newMode === 'preview') {
    console.log('📄 [sentence -> preview] 从逐句对照切换到整页预览')
    console.log('1️⃣ 先保存所有编辑...')
    await saveAllEdits()
    console.log('2️⃣ 开始同步到预览...')
    await syncToPreviewMode()
    console.log('✅ 同步完成，现在显示整页预览')
  } 
  // 从整页预览切换到逐句对照
  else if (oldMode === 'preview' && newMode === 'sentence') {
    console.log('📝 [preview -> sentence] 从整页预览切换到逐句对照')
    console.log('1️⃣ 加载句子数据...')
    if (sentences.value.length === 0) {
      console.log('句子数据为空，调用loadSentences')
      await loadSentences()
    } else {
      console.log(`✅ 已有 ${sentences.value.length} 个句子，跳过加载`)
    }
    console.log('✅ 逐句对照模式已就绪')
  } 
  else {
    console.log('⚠️ 未处理的切换:', oldMode, '->', newMode)
  }
}

// 加载文档预览（使用后端 PDF 预览，替代 vue-office）
const loadDocumentPreview = async (forceRefresh: boolean = false) => {
  try {
    console.log('开始加载文档预览（PDF），项目ID:', projectId.value, '强制刷新:', forceRefresh)
    console.log('目标语言:', currentTargetLanguage.value)

    if (!projectInfo.value || forceRefresh) {
      await loadProjectInfo()
    }

    // 原文：使用 PDF 预览接口
    try {
      sourceDocumentUrl.value = getDocumentPdfPreviewUrl(
        projectId.value,
        'source',
        undefined,
        forceRefresh
      )
      if (forceRefresh) {
        sourceDocumentUrl.value = `${sourceDocumentUrl.value}${sourceDocumentUrl.value.includes('?') ? '&' : '?'}_t=${Date.now()}`
      }
      sourceFileType.value = 'pdf'
      console.log('✅ 原文 PDF 预览 URL:', sourceDocumentUrl.value)
    } catch (error) {
      console.warn('⚠️ 获取原文 PDF 预览失败:', error)
      sourceDocumentUrl.value = ''
      sourceFileType.value = ''
    }

    // 译文：使用 PDF 预览接口（可能尚未生成，需优雅处理）
    if (currentTargetLanguage.value) {
      try {
        targetDocumentUrl.value = getDocumentPdfPreviewUrl(
          projectId.value,
          'target',
          currentTargetLanguage.value,
          forceRefresh
        )
        if (forceRefresh) {
          targetDocumentUrl.value = `${targetDocumentUrl.value}${targetDocumentUrl.value.includes('?') ? '&' : '?'}_t=${Date.now()}`
        }
        targetFileType.value = 'pdf'
        console.log('✅ 译文 PDF 预览 URL:', targetDocumentUrl.value)
      } catch (error) {
        console.warn('⚠️ 译文 PDF 不存在或获取失败（翻译未完成时正常）:', error)
        targetDocumentUrl.value = ''
        targetFileType.value = ''
      }
    }
  } catch (error) {
    console.error('加载文档预览失败:', error)
  }
}

// 页面级同步滚动（单向同步：译文滚动 → 原文跟随）
// 页面变化处理（保留以兼容）
const handleSourcePageChange = (page: number) => {
  console.log('原文页面变化:', page)
}

const handleTargetPageChange = (page: number) => {
  console.log('译文页面变化:', page)
}

// 滚动同步处理（译文滚动 → 原文跟随）
const handleSourceScroll = (_scrollTop: number) => {
  // 原文滚动不触发同步（单向同步：只从译文同步到原文）
}

const handleTargetScroll = (scrollTop: number) => {
  // 防止循环同步
  if (isSyncing.value) {
    return
  }
  
  // 检查原文查看器是否准备好
  if (!sourceViewer.value) {
    return
  }
  
  // 设置同步标志
  isSyncing.value = true
  
  // 使用防抖，避免过于频繁的同步
  setTimeout(() => {
    try {
      // 调用原文查看器的同步滚动方法
      if (sourceViewer.value && typeof sourceViewer.value.syncScrollPosition === 'function') {
        sourceViewer.value.syncScrollPosition(scrollTop)
      }
    } catch (error) {
      console.error('同步滚动失败:', error)
    } finally {
      // 重置同步标志
      setTimeout(() => {
        isSyncing.value = false
      }, 30)
    }
  }, 15) // 15ms防抖延迟
}

// 下载文档
const handleDownloadSource = () => {
  if (sourceDocumentUrl.value) {
    window.open(sourceDocumentUrl.value, '_blank')
  } else if (projectInfo.value?.file_url) {
    window.open(projectInfo.value.file_url, '_blank')
  }
}

const handleDownloadTarget = () => {
  if (targetDocumentUrl.value) {
    window.open(targetDocumentUrl.value, '_blank')
  } else if (projectInfo.value?.translated_document_url) {
    window.open(projectInfo.value.translated_document_url, '_blank')
  }
}


// 加载句子列表
const loadSentences = async () => {
  try {
    sentencesLoading.value = true
    console.log('开始加载句子列表')
    
    // 先尝试分句
    try {
      const segmentResult = await segmentDocuments(projectId.value)
      console.log('分句结果:', segmentResult)
    } catch (segmentError: any) {
      console.warn('分句处理:', segmentError?.response?.data || segmentError.message)
      // 如果分句失败，继续尝试获取句子
    }
    
    // 获取句子列表
    const response = await getSentences(projectId.value, {
      page: sentencesPage.value,
      limit: sentencesLimit.value
    })
    console.log('句子列表响应:', response)
    
    sentences.value = response.data || []
    sentencesTotal.value = response.total || 0
    
    console.log(`加载了 ${sentences.value.length} 个句子`)
    
    if (sentences.value.length === 0) {
      message.warning('暂无句子数据，请先完成文档翻译')
    }
    // 注意：不在逐句对照模式下自动提取热词，只在整页预览模式下提取
  } catch (error: any) {
    console.error('加载句子失败:', error)
    sentences.value = []
    sentencesTotal.value = 0
  } finally {
    sentencesLoading.value = false
  }
}

// 处理句子更新
const handleSentenceUpdate = async (update: { sentence_id: number; target_text: string }) => {
  try {
    // 验证 projectId 是否有效
    if (!projectId.value || isNaN(projectId.value)) {
      console.error('项目ID无效，无法保存:', projectId.value, '路由参数:', route.params)
      message.error('项目ID无效，请刷新页面重试')
      return
    }
    
    await updateSentences(projectId.value, [update])
    message.success('保存成功')
  } catch (error) {
    console.error('更新句子失败:', error)
  }
}

// 同步到整页预览
const syncToPreviewMode = async () => {
  try {
    syncing.value = true
    console.log('🔄 [syncToPreviewMode] 开始同步到整页预览模式')
    console.log('📊 项目ID:', projectId.value, '目标语言:', currentTargetLanguage.value)
    
    // 1. 检查是否有修改过的句子
    const hasModifiedSentences = sentences.value.some(s => s.is_modified === true)
    console.log('📝 检查是否有修改:', hasModifiedSentences ? '有修改' : '无修改')
    
    if (!hasModifiedSentences) {
      console.log('✅ 没有修改过的句子，直接跳转到整页预览模式（跳过重新生成流程）')
      syncing.value = false
      return  // 直接返回，不执行后续的重新生成流程
    }
    
    // 2. 有修改，执行重新生成流程
    console.log('📡 检测到修改，调用后端 syncToPreview API 重新生成译文文档...')
    if (!currentTargetLanguage.value) {
      message.warning('请先选择目标语言')
      return
    }
    const res = await syncToPreview(projectId.value, currentTargetLanguage.value)
    console.log('✅ 后端响应:', res)
    
    // 3. 重新加载项目信息（确保获取到最新的文件记录）
    await loadProjectInfo()
    
    // 4. 重新加载句子列表，同步后端的编辑状态清理（is_modified 已重置）
    await loadSentences()
    
    // 5. 重新加载文档预览（强制刷新，添加时间戳避免缓存）
    await loadDocumentPreview(true)
    
    // 6. 更新 targetViewerKey 强制重新渲染译文文档组件
    targetViewerKey.value++
    console.log('✅ 已触发预览刷新，targetViewerKey:', targetViewerKey.value)
    
    message.success({
      content: '同步完成，译文已更新',
      duration: 2,
      class: 'message-top-center'
    })
  } catch (error: any) {
    console.error('同步失败:', error)
  } finally {
    syncing.value = false
  }
}

// 保存所有编辑
const saveAllEdits = async () => {
  // 验证 projectId 是否有效
  if (!projectId.value || isNaN(projectId.value)) {
    console.error('项目ID无效，无法保存:', projectId.value, '路由参数:', route.params)
    message.error('项目ID无效，请刷新页面重试')
    return
  }
  
  // 遍历所有修改过的句子进行保存
  const modifiedSentences = sentences.value.filter(s => s.is_modified)
  
  if (modifiedSentences.length === 0) {
    return
  }
  
  try {
    const updates = modifiedSentences.map(s => ({
      sentence_id: s.id,
      target_text: s.target_text
    }))
    
    await updateSentences(projectId.value, updates)
    console.log(`已保存 ${updates.length} 个修改的句子`)
  } catch (error) {
    console.error('批量保存失败:', error)
    throw error
  }
}

// 处理句子保存（百度翻译模式：批量防抖保存）
const saveBuffer = ref<Map<number, SentenceUpdate>>(new Map())
const saveTimeout = ref<ReturnType<typeof setTimeout>>()

const handleSentenceSave = async (data: { id: number; text: string }) => {
  console.log('handleSentenceSave called:', data)
  
  // 添加到保存缓冲
  saveBuffer.value.set(data.id, {
    sentence_id: data.id,
    target_text: data.text
  })
  
  // 清除之前的定时器
  if (saveTimeout.value) {
    clearTimeout(saveTimeout.value)
  }
  
  // 防抖：500ms后批量保存
  saveTimeout.value = setTimeout(async () => {
    try {
      // 验证 projectId 是否有效
      if (!projectId.value || isNaN(projectId.value)) {
        console.error('项目ID无效，无法保存:', projectId.value, '路由参数:', route.params)
        message.error('项目ID无效，请刷新页面重试')
        return
      }
      
      const updates = Array.from(saveBuffer.value.values())
      console.log('准备批量保存，数量:', updates.length, '内容:', updates)
      
      if (updates.length > 0) {
        const result = await updateSentences(projectId.value, updates)
        console.log('批量保存API响应:', result)
        
        // 注意：不重置 is_modified，保持为 true
        // 因为数据虽然保存到数据库了，但还没有同步到译文文档
        // 需要在切换到整页预览（重新生成文档）时才重置
        
        saveBuffer.value.clear()
      }
    } catch (error) {
      console.error('批量保存句子失败:', error)
    }
  }, 500)
}

/**
 * 加载语言列表（用于 getLanguageName 等展示）
 * 使用 getLanguageOptions 获取全部语言并按中文拼音排序，与其它模块一致
 */
const loadLanguages = async () => {
  try {
    const options = await LanguageAPI.getLanguageOptions({ status: true })
    availableLanguages.value = options.map((option) => ({
      id: 0,
      chinese_name: option.label.split(' (')[0] ?? '',
      english_name: option.label.split(' (')[1]?.replace(')', '') ?? '',
      language_code: option.value,
      microsoft_code: option.microsoft_code,
      language_type: option.language_type,
      status: true,
      created_at: '',
      updated_at: ''
    }))
  } catch (error) {
    console.error('加载语言列表失败:', error)
  }
}

const loadHotWords = async () => {
  try {
    const results = await runApiBatch([
      (ctx) =>
        hotWordApi.getProjectHotWords(
          projectId.value,
          { target_language: currentTargetLanguage.value },
          ctx.requestConfig,
        ),
      (ctx) =>
        hotWordApi.getHotWordStats(
          projectId.value,
          currentTargetLanguage.value,
          ctx.requestConfig,
        ),
    ])
    if (results[0]?.status === 'rejected') {
      throw results[0].reason
    }
    if (results[1]?.status === 'rejected') {
      throw results[1].reason
    }
    hotWords.value = results[0].value.items
    hotWordStats.value = results[1].value
  } catch (error) {
    console.error('加载热词失败:', error)
  }
}

// 刷新热词
const refreshHotWords = async () => {
  await loadHotWords()
}

const showTaskSelection = () => {
  taskSelectionVisible.value = true
}

const handleTaskAssigned = async (_taskId: number) => {
  hasActiveTask.value = true
  await loadProjectInfo()
  message.success('任务创建成功，开始翻译工作')
}

const onWorkItemCompleted = async () => {
  hasActiveTask.value = false
  if (projectInfo.value) {
    projectInfo.value = { ...projectInfo.value, status: 'completed' as any }
  }
}

const onWorkItemAssigned = async () => {
  hasActiveTask.value = true
  await loadProjectInfo()
}

const onWorkItemSubmitted = async () => {
  await loadProjectInfo()
}

const startTranslationIfNeeded = async () => {
  hasActiveTask.value = true
  await autoStartTranslation()
}

const handleLanguageChange = async (language: string) => {
  currentTargetLanguage.value = language
  await loadHotWords()
}

// 自动开始翻译
const autoStartTranslation = async () => {
  if (translationLock.value) {
    console.log('翻译任务已在进行中，跳过重复请求')
    return
  }
  
  try {
    translationLock.value = true
    translationStatus.value = 'translating'
    translationError.value = ''
    
    if (!currentTargetLanguage.value) {
      throw new Error('目标语言未设置，无法开始翻译')
    }
    
    console.log('开始自动翻译（等待完成）:', projectId.value, currentTargetLanguage.value)
    
    // 调用翻译API，等待翻译完成（wait=true）
    const result = await startDocumentTranslation(
      projectId.value, 
      currentTargetLanguage.value,
      true,  // wait=true，等待翻译完成
      300    // 最大等待5分钟
    )
    
    console.log('翻译完成结果:', result)
    
    // 根据返回的状态处理结果
    if (result.status === 'completed' || result.status === 'started') {
      if (result.status === 'completed') {
        translationStatus.value = 'completed'
        message.success('翻译完成')
        
        // 重新加载项目信息以获取最新的译文文档URL
        await loadProjectInfo()
        // 刷新双文档预览，使译文立即显示在右侧面板
        await loadDocumentPreview(true)
        // 刷新 ActionBar：展示「完成项目 / 指派任务」（pipeline_stage=workspace_ready）
        await actionBarRef.value?.refresh()
        await loadReviewTaskContext()
      } else if (result.timeout) {
        // 超时情况，翻译仍在进行中
        translationStatus.value = 'translating'
        message.warning(`翻译进行中，已等待（超时），请稍后查看结果`)
        
        // 重新加载项目信息
        await loadProjectInfo()
        await actionBarRef.value?.refresh()
      } else {
        // 翻译已启动，但后端没有等待完成（不应该发生，因为wait=true）
        translationStatus.value = 'translating'
        message.info('翻译任务已启动，正在处理中...')
      }
    } else if (result.status === 'failed') {
      translationStatus.value = 'failed'
      translationError.value = result.message || '翻译任务失败'
      message.error(`机器翻译失败: ${translationError.value}`)
    }
    
  } catch (error: any) {
    console.error('自动翻译失败:', error)
    translationStatus.value = 'failed'
    translationError.value = error.message || '翻译失败'
  } finally {
    translationLock.value = false
  }
}

// 重试翻译（保留供后续或外部调用）
const _retryTranslation = async () => {
  translationStatus.value = 'idle'
  translationError.value = ''
  await autoStartTranslation()
}

const _machineTranslate = async () => {
  try {
    translating.value = true
    
    if (!currentTargetLanguage.value) {
      message.error('目标语言未设置，无法开始翻译')
      return
    }
    
    // 调用翻译API，等待翻译完成（wait=true）
    const result = await startDocumentTranslation(
      projectId.value, 
      currentTargetLanguage.value,
      true,  // wait=true，等待翻译完成
      300    // 最大等待5分钟
    )
    
    // 根据返回的状态处理结果
    if (result.status === 'completed' || result.status === 'started') {
      if (result.status === 'completed') {
        message.success('翻译完成')
        
        // 重新加载项目信息以获取最新的译文文档URL
        await loadProjectInfo()
        // 刷新双文档预览，使译文立即显示在右侧面板
        await loadDocumentPreview(true)
        await actionBarRef.value?.refresh()
        await loadReviewTaskContext()
        
        // 翻译完成后，自动提取热词（并行处理，不等待）
        await loadHotWords()
        if (hotWords.value.length === 0) {
          autoExtractHotWords().catch(error => {
            console.error('后台热词提取失败:', error)
          })
        }
      } else if (result.timeout) {
        // 超时情况，翻译仍在进行中
        message.warning(`翻译进行中，已等待${result.wait_time}秒（超时），请稍后查看结果`)
        
        // 重新加载项目信息
        await loadProjectInfo()
        await actionBarRef.value?.refresh()
      } else {
        message.info('翻译任务已启动，正在处理中...')
      }
    } else if (result.status === 'failed') {
      message.error(`机器翻译失败: ${result.message || '未知错误'}`)
    }
    
  } catch (error: any) {
    console.error('机器翻译失败:', error)
  } finally {
    translating.value = false
  }
}

// 自动提取热词（首次进入时或翻译启动时）
const autoExtractHotWords = async () => {
  if (!currentTargetLanguage.value) {
    return
  }
  
  // 检查是否正在提取中（防止重复调用）
  // 同时检查前端状态和翻译进度状态
  if (extractingHotWords.value) {
    return
  }
  
  // 检查翻译进度中是否显示正在提取热词
  if (translationProgress.value?.status === 'extracting_hot_words') {
    return
  }
  
  // 先重新加载热词数据，确保获取最新状态
  await loadHotWords()
  
  // 再次检查是否已有热词（如果已有热词，说明提取已完成）
  if (hotWords.value.length > 0) {
    return
  }
  
  // 再次检查翻译进度状态（可能在加载热词期间状态更新了）
  // 重新加载翻译进度，确保获取最新状态
  try {
    const progress = await getTranslationProgress(projectId.value)
    translationProgress.value = progress
    
    // 如果翻译进度显示正在提取热词，直接返回
    if (progress?.status === 'extracting_hot_words') {
      return
    }
  } catch (error) {
    // 忽略错误，继续执行
  }
  
  // 再次检查前端状态（可能在加载进度期间状态更新了）
  if (extractingHotWords.value) {
    return
  }
  
  extractingHotWords.value = true
  try {
    const request = {
      target_language: currentTargetLanguage.value,
      force_extract: false // 不强制提取，优先使用已存在的热词
    }
    
    await hotWordApi.extractHotWords(projectId.value, request)
    
    // 重新加载热词数据
    await loadHotWords()
    
    // 重新加载翻译进度，更新状态
    try {
      const progress = await getTranslationProgress(projectId.value)
      translationProgress.value = progress
    } catch (error) {
      // 忽略错误
    }
  } catch (error: any) {
    console.error('自动提取热词失败:', error)
    // 如果错误信息包含"正在提取"等关键词，说明后端正在提取中
    const errorMessage = error?.response?.data?.detail || error?.message || ''
    if (errorMessage.includes('正在提取') || errorMessage.includes('already extracting') || errorMessage.includes('extracting')) {
      // 后端正在提取中，重新加载翻译进度并更新状态
      try {
        const progress = await getTranslationProgress(projectId.value)
        translationProgress.value = progress
      } catch (progressError) {
        // 忽略错误
      }
      // 不抛出错误，因为后端正在处理中
      return
    }
    // 其他错误，可能需要用户处理
  } finally {
    extractingHotWords.value = false
  }
}

// 检查并自动提取热词（只在加载句子后调用，避免重复）
const _checkAndAutoExtractHotWords = async () => {
  if (!currentTargetLanguage.value) {
    return
  }
  
  // 防止重复调用：如果正在提取中，直接返回
  if (extractingHotWords.value) {
    return
  }
  
  // 检查翻译进度中是否显示正在提取热词
  if (translationProgress.value?.status === 'extracting_hot_words') {
    return
  }
  
  try {
    // 先确保热词数据已加载（获取最新状态）
    await loadHotWords()
    
    // 再次检查是否正在提取中（可能在加载热词期间状态更新了）
    if (extractingHotWords.value || translationProgress.value?.status === 'extracting_hot_words') {
      return
    }
    
    // 检查是否已有热词（如果已有热词，不需要提取）
    if (hotWords.value.length > 0) {
      return
    }
    
    // 检查是否有翻译数据（检查句子中是否有 target_text 或项目是否有翻译文档）
    // 需要同时检查 translated_document_storage_id（OSS模式）和 translated_document_url（旧模式）
    const hasTranslatedData = sentences.value.some(sentence => 
      sentence.target_text && sentence.target_text.trim() !== ''
    ) || projectInfo.value?.translated_document_storage_id || projectInfo.value?.translated_document_url
    
    // 只要有翻译数据（翻译存在），且没有热词，且不在提取中，就自动提取热词
    if (hasTranslatedData) {
      // 不等待热词提取完成，让它后台运行
      autoExtractHotWords().catch(error => {
        console.error('后台热词提取失败:', error)
      })
    }
  } catch (error) {
    console.error('检查热词失败:', error)
  }
}

// 手动提取热词（保留函数，但按钮已隐藏）
const _extractHotWords = async () => {
  if (!currentTargetLanguage.value) {
    message.warning('请先选择目标语言')
    return
  }

  extractingHotWords.value = true
  try {
    const request = {
      target_language: currentTargetLanguage.value,
      force_extract: false // 不强制提取，优先使用已存在的热词
    }
    
    await hotWordApi.extractHotWords(projectId.value, request)
    
    // 重新加载热词数据
    await loadHotWords()
    
    message.success('热词提取完成')
  } catch (error) {
    console.error('Extract hot words error:', error)
  } finally {
    extractingHotWords.value = false
  }
}

// 开始编辑热词
const startHotWordEdit = (hotWord: HotWord) => {
  if (isEditingDisabled.value) return
  
  editingHotWordId.value = hotWord.id
  editingHotWordText.value = hotWord.translated_word || ''
  
  nextTick(() => {
    if (hotWordInput.value) {
      hotWordInput.value.focus()
    }
  })
}

// 保存热词编辑
const saveHotWordEdit = async (hotWord: HotWord) => {
  try {
    await hotWordApi.updateHotWord(hotWord.id, {
      translated_word: editingHotWordText.value,
      status: 'edited'
    })
    
    editingHotWordId.value = -1
    await loadHotWords()
    message.success('热词更新成功')
  } catch (error) {
    console.error('更新热词失败:', error)
  }
}

// 处理热词编辑失焦
const handleHotWordBlur = (hotWord: HotWord) => {
  saveHotWordEdit(hotWord)
}

// 确认热词
const confirmHotWord = async (hotWord: HotWord) => {
  try {
    // 根据当前状态决定确认后的状态
    let newStatus: 'ai_suggested' | 'confirmed' | 'edited'
    if (hotWord.status === 'ai_suggested') {
      newStatus = 'confirmed'
    } else if (hotWord.status === 'edited') {
      newStatus = 'confirmed' // 已编辑状态确认后变成译员确认
    } else {
      newStatus = hotWord.status as 'ai_suggested' | 'confirmed' | 'edited' // 其他状态保持不变
    }
    
    await hotWordApi.updateHotWord(hotWord.id, {
      status: newStatus
    })
    
    await loadHotWords()
    message.success('热词已由译员确认')
  } catch (error) {
    console.error('确认热词失败:', error)
  }
}

// 确认所有热词
const confirmAllHotWords = async () => {
  try {
    const unconfirmedHotWords = hotWords.value.filter(hw => 
      hw.status === 'ai_suggested' || hw.status === 'edited'
    )
    
    if (unconfirmedHotWords.length === 0) {
      message.info('没有需要确认的热词')
      return
    }
    
    // 批量确认所有未确认的热词（AI建议和已编辑状态）
    const confirmPromises = unconfirmedHotWords.map(hotWord => 
      hotWordApi.updateHotWord(hotWord.id, { status: 'confirmed' })
    )
    
    // 写操作批量确认，允许 Promise.all（非首屏并行）
    await Promise.all(confirmPromises)
    
    await loadHotWords()
    message.success(`译员确认 ${unconfirmedHotWords.length} 个热词`)
  } catch (error) {
    console.error('批量确认热词失败:', error)
  }
}

// 删除热词
const deleteHotWord = async (hotWord: HotWord) => {
  try {
    await hotWordApi.deleteHotWord(hotWord.id)
    await loadHotWords()
    message.success('热词已删除')
  } catch (error) {
    console.error('删除热词失败:', error)
  }
}

// 获取热词状态颜色
const getHotWordStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    ai_suggested: 'blue',
    edited: 'orange',
    confirmed: 'green'
  }
  return colors[status] || 'default'
}

// 获取热词状态文本
const getHotWordStatusText = (status: string) => {
  const texts: Record<string, string> = {
    ai_suggested: t('document.hotwordStatusAiSuggested'),
    edited: t('document.hotwordStatusEdited'),
    confirmed: t('document.hotwordStatusConfirmed'),
  }
  return texts[status] || status
}

// AI热词提取处理方法
const _handleHotWordsExtracted = (hotWords: any[]) => {
  message.success(`AI成功提取 ${hotWords.length} 个热词`)
  // 切换到热词管理标签页
  hotwordActiveTab.value = 'management'
  // 重新加载热词列表
  loadHotWords()
}

const _handleTranslationsCompleted = (translations: any[]) => {
  message.success(`AI成功翻译 ${translations.length} 个热词`)
  // 重新加载热词列表
  loadHotWords()
}

const _highlightHotWords = (text: string) => {
  let highlightedText = text
  
  hotWords.value.forEach(hotword => {
    const regex = new RegExp(`\\b${hotword.original_word}\\b`, 'gi')
    highlightedText = highlightedText.replace(regex, 
      `<span class="hotword-highlight" title="${hotword.translated_word}">$&</span>`
    )
  })
  
  return highlightedText
}

const _startEditingHotword = async (id: number) => {
  editingHotword.value = id

  await nextTick()
  if (hotwordInputRef.value?.[0]) {
    hotwordInputRef.value[0].focus()
  }
}

const _finishEditingHotword = async () => {
  const id = editingHotword.value
  editingHotword.value = -1

  if (id > 0) {
    const hotword = hotWords.value.find(hw => hw.id === id)
    if (hotword) {
      try {
        await hotWordApi.updateHotWord(id, {
          translated_word: hotword.translated_word,
          status: 'edited'
        })
        await loadHotWords()
      } catch (error) {
        console.error('更新热词失败:', error)
      }
    }
  }
}

const _confirmHotword = async (id: number) => {
  try {
    await hotWordApi.updateHotWord(id, { status: 'confirmed' })
    await loadHotWords()
    message.success('热词已由译员确认')
  } catch (error) {
    console.error('确认热词失败:', error)
  }
}

const _deleteHotword = async (id: number) => {
  try {
    await hotWordApi.deleteHotWord(id)
    await loadHotWords()
    message.success('热词已删除')
  } catch (error) {
    console.error('删除热词失败:', error)
  }
}

const showAddHotwordModal = () => {
  addHotwordForm.value = {
    original_word: '',
    translated_word: ''
  }
  addHotwordModalVisible.value = true
}

const addHotword = async () => {
  try {
    // 验证必填字段
    if (!addHotwordForm.value.original_word.trim() || !addHotwordForm.value.translated_word.trim()) {
      message.error('请填写完整的热词信息')
      return
    }

    if (!currentTargetLanguage.value) {
      message.error('请先选择目标语言')
      return
    }

    // 使用统一的热词提取接口，传入手动添加的热词数据
    await hotWordApi.extractHotWords(projectId.value, {
      target_language: currentTargetLanguage.value,
      manual_hot_word: {
        original_word: addHotwordForm.value.original_word.trim(),
        translated_word: addHotwordForm.value.translated_word.trim()
      }
    })
    
    await loadHotWords()
    addHotwordModalVisible.value = false
    message.success('热词添加成功')
  } catch (error) {
    console.error('添加热词失败:', error)
  }
}

const resetAddHotwordForm = () => {
  addHotwordForm.value = {
    original_word: '',
    translated_word: ''
  }
}

const completeTranslation = async () => {
  try {
    completing.value = true
    
    // 调用文档翻译完成API
    const result = await completeDocumentTranslation(projectId.value)
    
    // 任务完成通知已由后端自动发送，前端不再发送
    
    message.success(result.message)
    
    // 清理项目缓存，防止重新进入文档上传页面时自动跳转
    localStorage.removeItem('current_document_project')
    
    // 检查项目信息中的任务类型来决定跳转
    if (projectInfo.value?.assignee_id && projectInfo.value?.assignee_id !== projectInfo.value?.creator_id) {
      // 指派任务：被指派人完成 → 跳转到「收到的任务」（避免 TASK_ASSIGN 权限 403）
      router.push('/task/received')
    } else {
      // 自己完成：2 秒后跳转到「我的项目」
      setTimeout(() => {
        router.push('/project/my-projects')
      }, 2000)
    }
  } catch (error: any) {
    console.error('完成翻译失败:', error)
  } finally {
    completing.value = false
  }
}

const exportDocument = async () => {
  try {
    // 检查项目状态
    if (!projectInfo.value) {
      message.error('项目信息不存在')
      return
    }
    
    // 检查是否有翻译后的文档（只使用 translated_document_storage_id）
    if (!projectInfo.value.translated_document_storage_id) {
      message.warning('没有找到翻译后的文档存储ID，请先完成翻译或同步到预览')
      return
    }
    
    exportingDocument.value = true
    const hideLoading = message.loading('正在下载翻译文档...', 0)
    
    try {
      // 使用 file_storage_id 下载
      const fileStorageId = projectInfo.value.translated_document_storage_id
      const response = await downloadTranslatedDocument(fileStorageId)
      
      hideLoading()
      
      // 从Axios响应中获取Blob数据
      const blob = response.data
      
      // 检查响应是否为有效的 Blob
      if (!(blob instanceof Blob)) {
        console.error('API返回的不是Blob对象:', blob)
        message.error('下载失败：服务器返回的数据格式不正确')
        return
      }
      
      // 检查 Blob 大小
      if (blob.size === 0) {
        console.error('下载的文件为空')
        message.error('下载失败：文件为空')
        return
      }
      
      // 从响应头中提取文件名（支持 RFC 5987 编码）
      let filename = '翻译文档.docx' // 默认文件名
      
      try {
        // 获取 Content-Disposition 头（Axios 会将响应头转换为小写）
        const contentDisposition = response.headers?.['content-disposition'] || 
                                   response.headers?.['Content-Disposition'] ||
                                   ''
        
        if (contentDisposition) {
          // 尝试解析 RFC 5987 编码的文件名 (filename*=UTF-8''...)
          const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
          if (rfc5987Match) {
            filename = decodeURIComponent(rfc5987Match[1])
          } else {
            // 尝试解析标准格式 (filename="...")
            const standardMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
            if (standardMatch) {
              filename = standardMatch[1].replace(/^"|"$/g, '') // 移除引号
            }
          }
        }
      } catch (error) {
        // 解析失败，使用默认文件名或从项目信息中获取
      }
      
      // 如果仍然使用默认文件名，尝试从原文档信息中获取
      if (filename === '翻译文档.docx') {
        // 优先从项目信息中获取原文件名
        const originalFileName = projectInfo.value.file_name || ''
        if (originalFileName) {
          filename = originalFileName
        } else {
          // 尝试从原文档URL中获取文件名
          const originalDocUrl = projectInfo.value.document_url || projectInfo.value.file_url || ''
          if (originalDocUrl) {
            const urlParts = originalDocUrl.split('/')
            const urlFilename = urlParts[urlParts.length - 1]
            if (urlFilename && urlFilename.includes('.')) {
              // 直接使用原文件名（不添加 _translated 后缀）
              filename = urlFilename
            }
          }
        }
      }
      
      // 创建下载链接
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      message.success('翻译文档下载成功！')
      
    } catch (error: any) {
      hideLoading()
      console.error('下载翻译文档失败:', error)
    } finally {
      exportingDocument.value = false
      hideLoading()
    }
    
  } catch (error) {
    console.error('导出文档失败:', error)
    exportingDocument.value = false
  }
}

// 获取文件扩展名
const _getFileExtension = (filePath?: string): string => {
  if (!filePath) {
    return '.docx' // 默认扩展名
  }
  const parts = filePath.split('.')
  if (parts.length > 1) {
    return '.' + parts[parts.length - 1]
  }
  return '.docx' // 默认扩展名
}



const getLanguageName = (langCode: string) => {
  const lang = availableLanguages.value.find(l => l.language_code === langCode)
  return lang ? lang.chinese_name : langCode
}

const _getHistoryColor = (action: string) => {
  const colors: Record<string, string> = {
    'create': 'green',
    'update': 'blue',
    'delete': 'red',
    'translate': 'orange'
  }
  return colors[action] || 'gray'
}

const _getHistoryActionText = (action: string) => {
  const texts: Record<string, string> = {
    'create': '创建',
    'update': '更新',
    'delete': '删除',
    'translate': '翻译'
  }
  return texts[action] || action
}

const _formatTime = (time: string) => {
  return new Date(time).toLocaleString()
}

// 暴露供模板或后续扩展使用，避免 no-unused 告警
defineExpose({
  targetViewer,
  _userStore,
  _editingTextareaRef,
  _filteredHotWords,
  _retryTranslation,
  _machineTranslate,
  _checkAndAutoExtractHotWords,
  _extractHotWords,
  _handleHotWordsExtracted,
  _handleTranslationsCompleted,
  _highlightHotWords,
  _startEditingHotword,
  _finishEditingHotword,
  _confirmHotword,
  _deleteHotword,
  _getFileExtension,
  _getHistoryColor,
  _getHistoryActionText,
  _formatTime
})

</script>

<style lang="less" scoped>
.document-translation {
  height: calc(100vh - 64px); /* 减去顶部header的高度 */
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0; /* 关键：允许flex容器收缩，防止横向滚动条 */
  background: #f5f5f5;
  margin: -24px 0; /* 向上下各偏移24px，抵消父容器的padding */
  padding: 0;
  overflow: hidden; /* 防止页面滚动 */
  overflow-x: hidden !important; /* 明确禁止横向滚动条 */
}

.toolbar {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 12px 16px !important; /* 减少内边距以适应小屏幕 */
  background: white !important;
  border-bottom: 1px solid #f0f0f0 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02) !important;
  min-height: 56px !important; /* 减少最小高度 */
  height: 56px !important; /* 减少固定高度，节省空间 */
  margin: 0 !important; /* 确保没有外边距 */
  position: relative !important; /* 确保定位正确 */
  top: 0 !important; /* 确保在顶部 */
  left: 0 !important;
  right: 0 !important;
  z-index: 1000 !important; /* 确保在最上层 */
  flex-shrink: 0 !important; /* 防止被压缩 */
}

.toolbar-left {
  display: flex !important;
  align-items: center !important;
  height: 100% !important;
  
  .page-title {
    margin: 0 !important;
    font-size: 16px !important; /* 减小字体以适应小屏幕 */
    font-weight: 600 !important;
    color: #262626 !important;
    line-height: 1.2 !important;
  }
}

.toolbar-right {
  display: flex !important;
  align-items: center !important;
  height: 100% !important;
  
  .ant-space {
    align-items: center !important;
    
    .ant-btn {
      height: 32px !important; /* 统一按钮高度 */
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      
      .anticon {
        font-size: 14px !important;
      }
    }
    
    .ant-select {
      height: 32px !important; /* 统一选择框高度 */
      
      .ant-select-selector {
        height: 32px !important;
        display: flex !important;
        align-items: center !important;
      }
    }
  }
}

.main-content {
  flex: 1;
  width: 100%;
  min-width: 0; /* 关键：允许flex容器收缩，防止横向滚动条 */
  overflow: hidden;
  overflow-x: hidden !important; /* 明确禁止横向滚动条 */
  display: flex; /* 确保 flex 布局正常传递 */
  flex-direction: column; /* 确保垂直布局 */
  
  /* 修复 Ant Design Spin 组件的高度问题 */
  :deep(.ant-spin-nested-loading) {
    height: 100%;
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
  }
  
  :deep(.ant-spin-container) {
    height: 100%;
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-x: hidden;
  }
}

.translation-workspace {
  display: flex;
  height: 100%;
  flex: 1; /* 修改为 flex: 1 以自动填充父容器 */
  width: 100%;
  min-width: 0; /* 关键：允许flex容器收缩，防止横向滚动条 */
  gap: 1px;
  background: #e8e8e8;
  overflow: hidden; /* 防止工作区滚动 */
  overflow-x: hidden; /* 明确禁止横向滚动条 */
}

/* 逐句对照模式布局 */
.sentence-mode {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: white;
}

.source-panel,
.translation-panel,
.hotword-panel {
  background: white;
  display: flex;
  flex-direction: column;
}

.source-panel {
  flex: 1;
  min-width: 300px;
}

.translation-panel {
  flex: 1;
  min-width: 300px;
}

.hotword-panel {
  max-width: 280px;
  min-width: 220px;
}

/* 整页预览模式的三栏布局 */
.preview-mode {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0; /* 关键：允许flex容器收缩，防止横向滚动条 */
  height: 100%;
  overflow: hidden;
  overflow-x: hidden !important; /* 明确禁止横向滚动条 */
}

.preview-mode .document-panels {
  display: flex;
  height: 100%;
  width: 100%; /* 确保占满宽度 */
  min-width: 0; /* 关键：允许flex容器收缩，防止横向滚动条 */
  gap: 1px;
  background: #e8e8e8;
  overflow: hidden;
  overflow-x: hidden; /* 明确禁止横向滚动条 */
  flex: 1; /* 确保占满剩余空间 */
}

.preview-mode .panel {
  background: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%; /* 确保面板占满分配的空间 */
  min-width: 0; /* 关键：允许flex子元素收缩，防止横向滚动条 */
  height: 100%; /* 确保面板占满高度 */
}

.preview-mode .source-panel {
  flex: 1 1 0; /* flex-grow: 1, flex-shrink: 1, flex-basis: 0 - 平均分配剩余空间 */
  min-width: 0; /* 关键：允许收缩，防止横向滚动条 */
  max-width: none; /* 移除最大宽度限制 */
}

.preview-mode .target-panel {
  flex: 1 1 0; /* flex-grow: 1, flex-shrink: 1, flex-basis: 0 - 平均分配剩余空间 */
  min-width: 0; /* 关键：允许收缩，防止横向滚动条 */
  max-width: none; /* 移除最大宽度限制 */
}

.preview-mode .hotword-panel {
  width: 280px;
  min-width: 0; /* 关键：允许收缩，防止横向滚动条 */
  max-width: 280px; /* 最大宽度限制 */
  flex-shrink: 1; /* 允许收缩，防止横向滚动条 */
  flex-grow: 0; /* 不允许扩展 */
  background: white;
  border-left: 1px solid #e8e8e8;
}

/* 小屏幕适配：当屏幕宽度较小时，热词面板自动缩小 */
@media (max-width: 1200px) {
  .preview-mode .hotword-panel {
    width: 240px;
    max-width: 240px;
  }
}

@media (max-width: 900px) {
  .preview-mode .hotword-panel {
    width: 200px;
    max-width: 200px;
  }
}

.preview-mode .panel h3 {
  margin: 0;
  padding: 12px 16px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  flex-shrink: 0;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px; /* 进一步减少内边距以适应小屏幕 */
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
  height: 48px; /* 减少高度以节省空间 */
  flex-shrink: 0; /* 防止被压缩 */

  h3 {
    margin: 0;
    font-size: 14px; /* 减小字体 */
    font-weight: 600;
    color: #262626;
  }

  .panel-actions {
    display: flex;
    gap: 4px;
    align-items: center;
    height: 32px; /* 固定按钮区域高度 */

    .ant-btn.active {
      color: #1890ff;
      background: #e6f7ff;
    }

    .header-spacer {
      width: 80px; /* 占位空间，与"机器翻译"按钮宽度相近 */
      height: 32px; /* 与按钮高度一致 */
    }
  }
}

.panel-content {
  flex: 1; /* 使用flex自动填充剩余空间 */
  overflow: hidden;
  overflow-x: hidden !important; /* 明确禁止横向滚动条 */
  display: flex; /* 确保子元素也能使用flex布局 */
  flex-direction: column;
  width: 100%; /* 确保占满面板宽度 */
  min-width: 0; /* 关键：允许flex子元素收缩，防止横向滚动条 */
  height: 100%; /* 确保占满面板高度 */
  position: relative; /* 确保定位上下文 */
  
  // DocumentViewer组件样式
  :deep(.document-viewer) {
    width: 100%;
    height: 100%;
    min-width: 0; /* 允许收缩 */
  }
  
  // 空状态样式
  .no-document {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fafafa;
  }
}


.document-content {
  height: 100%;
  overflow-y: auto;
  padding: 16px;

  .paragraph {
    display: flex;
    margin-bottom: 16px;
    padding: 12px;
    border-radius: 6px;
    transition: all 0.2s;
    cursor: pointer;

    &:hover {
      background: #f8f9fa;
    }

    &.highlighted {
      background: #e6f7ff;
      border: 1px solid #91d5ff;
      box-shadow: 0 0 8px rgba(24, 144, 255, 0.3);
      transform: scale(1.02);
    }

    &.editing {
      background: #fff7e6;
      border: 1px solid #ffd666;
    }

    &.editable.modified {
      border-left: 3px solid #52c41a;
    }

    .paragraph-number {
      width: 32px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f0f0f0;
      border-radius: 4px;
      font-size: 12px;
      color: #8c8c8c;
      margin-right: 12px;
      flex-shrink: 0;
    }

    .paragraph-text,
    .paragraph-content {
      flex: 1;
      line-height: 1.6;
      color: #262626;
    }

    .modification-indicator {
      margin-left: 8px;
      
      .modified-icon {
        color: #52c41a;
      }
    }
  }
}

.source-content .paragraph {
  border-left: 3px solid #1890ff;
}

.translation-content .paragraph {
  border-left: 3px solid #52c41a;
}

.hotword-search {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.hotword-stats {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.hotword-list {
  flex: 1;
  overflow-y: hidden;
  padding: 8px;

  .hotword-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    margin-bottom: 4px;
    border-radius: 4px;
    border: 1px solid transparent;
    transition: all 0.2s;

    &:hover {
      background: #f8f9fa;
      border-color: #e8e8e8;
    }

    &.ai-suggested {
      background: #f6ffed;
      border-color: #b7eb8f;
    }

    &.confirmed {
      background: #e6f7ff;
      border-color: #91d5ff;
    }

    &.edited {
      background: #fff7e6;
      border-color: #ffd666;
    }

    .hotword-content {
      flex: 1;
      min-width: 0;

      .original-word {
        font-weight: 500;
        color: #262626;
        margin-bottom: 2px;
        word-break: break-word;
      }

      .translated-word {
        font-size: 12px;
        color: #8c8c8c;
        cursor: pointer;
        word-break: break-word;

        &:hover {
          color: #1890ff;
        }
      }
    }

    .hotword-actions {
      display: flex;
      gap: 4px;
      margin-left: 8px;
    }
  }
}

.add-hotword {
  padding: 16px;
  border-top: 1px solid #f0f0f0;
}

.no-task-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  margin: 24px;
  border-radius: 8px;
}

.editing-history {
  max-height: 400px;
  overflow-y: auto;

  .history-item {
    .history-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;

      .action {
        font-weight: 500;
        color: #262626;
      }

      .time {
        font-size: 12px;
        color: #8c8c8c;
      }
    }

    .history-content {
      color: #595959;
      font-size: 14px;
    }
  }
}

:deep(.hotword-highlight) {
  background: #fff2e8;
  color: #fa8c16;
  padding: 1px 2px;
  border-radius: 2px;
  cursor: help;
}

:deep(.ant-empty-img-simple) {
  margin-bottom: 16px;
}

.hotword-card {
  height: 100%;

  :deep(.ant-card-head) {
    min-height: 48px; /* 与 panel-header 保持一致 */
    padding: 0 12px;
    display: flex;
    align-items: center; /* 垂直居中标题 */
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
  }

  :deep(.ant-card-head-title) {
    padding: 0;
    font-size: 14px; /* 与 panel 标题一致 */
    font-weight: 600;
    color: #262626;
  }

  :deep(.ant-card-body) {
    height: calc(100% - 48px); /* 调整为与panel-header一致的高度 */
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding: 8px; /* 减少内边距 */
  }

  .hotword-stats {
    margin-bottom: 4px;
    
    :deep(.ant-statistic) {
      .ant-statistic-title {
        font-size: 12px;
        margin-bottom: 2px;
      }
      
      .ant-statistic-content {
        font-size: 16px;
        line-height: 1.1;
      }
    }
  }

  .hotword-list {
    flex: 1;
    margin-bottom: 0;
    margin-top: 4px;
    min-height: 220px; /* 增加最小高度，扩大词条可视区域 */
    max-height: calc(100vh - 320px); /* 提高上限，使显示框更高 */
    height: auto; /* 自动高度 */
    flex-shrink: 1; /* 允许收缩 */

    :deep(.ant-table) {
      height: 100%;
      
      .ant-table-container {
        height: 100%;
      }
      
      .ant-table-body {
        max-height: 600px !important; /* 提高表格体高度，显示更多词条 */
        overflow-y: auto;
      }
      
      
      .ant-table-thead > tr > th {
        background: #fafafa;
        font-weight: 600;
        font-size: 12px;
        padding: 8px 4px;
        height: 40px;
      }
      
      .ant-table-tbody > tr > td {
        padding: 12px 4px;
        font-size: 12px;
        height: 48px;
        vertical-align: middle;
      }
      
      .ant-table-tbody > tr:hover > td {
        background: #f5f5f5;
      }
    }

    .hotword-text {
      font-weight: 500;
      color: #333;
    }

    .translated-word-clickable {
      cursor: pointer;
      padding: 2px 4px;
      border-radius: 4px;
      transition: background 0.3s;
      display: block;
      min-height: 20px;

      &:hover {
        background: #f0f8ff;
        color: #1890ff;
      }

      &.disabled {
        cursor: not-allowed;
        color: #999;
        
        &:hover {
          background: transparent;
          color: #999;
        }
      }
    }
  }

  .hotword-actions {
    margin-top: 0;
    padding-top: 8px;
    padding-bottom: 8px;
    border-top: 1px solid #f0f0f0;
    flex-shrink: 0; /* 防止按钮被压缩 */
  }
}

/* 双文档对照工作区 */
.dual-document-workspace {
  height: calc(100vh - 200px);
  min-height: 600px;
}

/* 翻译状态显示样式 */
.translation-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
  background: #fafafa;
  
  p {
    margin: 0;
    color: #666;
    font-size: 14px;
  }
}

.translation-error {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}


/* 提示框垂直居中显示 */
:deep(.message-top-center) {
  top: 50% !important;
  margin-top: -16px !important;
}

/* 同步进度提示框样式 - 缩小并居中 */
:deep(.sync-progress-modal) {
  .ant-modal {
    padding-bottom: 0;
    top: 50%;
    transform: translateY(-50%);
  }
  
  .ant-modal-content {
    padding: 24px;
    border-radius: 8px;
  }
  
  .ant-modal-body {
    padding: 0;
  }
}

.sync-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  text-align: center;
  min-height: 120px;
  
  :deep(.ant-spin) {
    margin-bottom: 12px;
  }
  
  p {
    margin: 0;
    font-size: 14px;
    color: #666;
    line-height: 1.5;
  }
}
</style>